import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';

// Debug event types
export type LogLevel = 'log' | 'info' | 'warn' | 'error';
export interface DebugLog {
  id: string;
  timestamp: number;
  level: LogLevel;
  message: string;
  args?: any[];
}

export interface NetworkLog {
  id: string;
  timestamp: number;
  method: string;
  url: string;
  status?: number;
  ok?: boolean;
  durationMs?: number;
  error?: string;
}

export interface ActionLog {
  id: string;
  timestamp: number;
  type: string;
  payload?: any;
}

interface DebugContextValue {
  debugEnabled: boolean;
  setDebugEnabled: (v: boolean) => void;
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
  logs: DebugLog[];
  addLog: (entry: Omit<DebugLog, 'id' | 'timestamp'>) => void;
  clearLogs: () => void;
  errors: DebugLog[];
  actions: ActionLog[];
  addAction: (type: string, payload?: any) => void;
  network: NetworkLog[];
  addNetwork: (entry: Omit<NetworkLog, 'id' | 'timestamp'>) => void;
}

const DebugContext = createContext<DebugContextValue | undefined>(undefined);

const MAX_LOGS = 500;

export function DebugProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [debugEnabled, setDebugEnabled] = useState<boolean>(() => {
    try {
      const url = new URL(window.location.href);
      const qp = url.searchParams.get('debug');
      if (qp === '1') return true;
      if (qp === '0') return false;
      const stored = localStorage.getItem('debugEnabled');
      return stored ? stored === 'true' : false;
    } catch {
      return false;
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  const [logs, setLogs] = useState<DebugLog[]>([]);
  const [errors, setErrors] = useState<DebugLog[]>([]);
  const [actions, setActions] = useState<ActionLog[]>([]);
  const [network, setNetwork] = useState<NetworkLog[]>([]);

  const originals = useRef<{ console?: any; fetch?: any }>({});

  useEffect(() => {
    localStorage.setItem('debugEnabled', String(debugEnabled));
  }, [debugEnabled]);

  const addLog = useCallback((entry: Omit<DebugLog, 'id' | 'timestamp'>) => {
    setLogs(prev => {
      const next = [{ id: crypto.randomUUID(), timestamp: Date.now(), ...entry }, ...prev].slice(0, MAX_LOGS);
      return next;
    });
    if (entry.level === 'error') {
      setErrors(prev => [{ id: crypto.randomUUID(), timestamp: Date.now(), ...entry }, ...prev].slice(0, MAX_LOGS));
    }
  }, []);

  const clearLogs = useCallback(() => {
    setLogs([]);
    setErrors([]);
  }, []);

  const addAction = useCallback((type: string, payload?: any) => {
    setActions(prev => [{ id: crypto.randomUUID(), timestamp: Date.now(), type, payload }, ...prev].slice(0, MAX_LOGS));
  }, []);

  const addNetwork = useCallback((entry: Omit<NetworkLog, 'id' | 'timestamp'>) => {
    setNetwork(prev => [{ id: crypto.randomUUID(), timestamp: Date.now(), ...entry }, ...prev].slice(0, MAX_LOGS));
  }, []);

  // Patch console and global errors when enabled
  useEffect(() => {
    if (!debugEnabled) {
      // restore if patched previously
      if (originals.current.console) {
        console.log = originals.current.console.log;
        console.info = originals.current.console.info;
        console.warn = originals.current.console.warn;
        console.error = originals.current.console.error;
        originals.current.console = undefined;
      }
      if (originals.current.fetch) {
        window.fetch = originals.current.fetch;
        originals.current.fetch = undefined;
      }
      return;
    }

    // Patch console
    if (!originals.current.console) {
      originals.current.console = {
        log: console.log,
        info: console.info,
        warn: console.warn,
        error: console.error,
      };
      console.log = (...args: any[]) => {
        addLog({ level: 'log', message: args?.[0]?.toString?.() ?? 'log', args });
        originals.current.console.log(...args);
      };
      console.info = (...args: any[]) => {
        addLog({ level: 'info', message: args?.[0]?.toString?.() ?? 'info', args });
        originals.current.console.info(...args);
      };
      console.warn = (...args: any[]) => {
        addLog({ level: 'warn', message: args?.[0]?.toString?.() ?? 'warn', args });
        originals.current.console.warn(...args);
      };
      console.error = (...args: any[]) => {
        addLog({ level: 'error', message: args?.[0]?.toString?.() ?? 'error', args });
        originals.current.console.error(...args);
      };
    }

    // Global error capture
    const onError = (event: ErrorEvent) => addLog({ level: 'error', message: event.message, args: [event.error] });
    const onRejection = (event: PromiseRejectionEvent) => addLog({ level: 'error', message: 'Unhandled rejection', args: [event.reason] });
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    // Patch fetch
    if (!originals.current.fetch) {
      originals.current.fetch = window.fetch;
      window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
        const start = performance.now();
        const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
        const url = typeof input === 'string' ? input : (input as any).url ?? String(input);
        try {
          const res = await (originals.current.fetch as typeof window.fetch)(input as any, init);
          const durationMs = Math.round(performance.now() - start);
          addNetwork({ method, url, status: (res as Response).status, ok: (res as Response).ok, durationMs });
          return res;
        } catch (e: any) {
          const durationMs = Math.round(performance.now() - start);
          addNetwork({ method, url, error: e?.message ?? String(e), durationMs });
          throw e;
        }
      }) as any;
    }

    // Keyboard shortcut to toggle panel
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === '`') {
        setIsOpen(v => !v);
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, [debugEnabled, addLog, addNetwork]);

  // Auto-enable for admins if present
  useEffect(() => {
    // If user has metadata role admin, enable by default (non-destructive)
    const role = (user as any)?.user_metadata?.role || (user as any)?.app_metadata?.role;
    if (role === 'admin' && localStorage.getItem('debugEnabled') === null) {
      setDebugEnabled(true);
    }
  }, [user]);

  const value = useMemo<DebugContextValue>(() => ({
    debugEnabled,
    setDebugEnabled,
    isOpen,
    setIsOpen,
    logs,
    addLog,
    clearLogs,
    errors,
    actions,
    addAction,
    network,
    addNetwork,
  }), [debugEnabled, isOpen, logs, errors, actions, network, addLog, addAction, addNetwork, clearLogs]);

  return (
    <DebugContext.Provider value={value}>
      {children}
    </DebugContext.Provider>
  );
}

export function useDebugContext() {
  const ctx = useContext(DebugContext);
  if (!ctx) throw new Error('useDebugContext must be used within DebugProvider');
  return ctx;
}
